import { Injectable } from '@angular/core';
import { ThemePalette } from '@angular/material/core';
import { ProcessRemoteControllerService, ServerSideEventRemoteControllerService } from 'core/api';
import { EProcessStatus, IProcessDto } from 'core/dto';
import { createPersistantObservable, subjectFromObs } from 'core/rx-js';
import { BroadcastChannelService } from 'core/services';
import { dateUtils } from 'core/utils';
import { BehaviorSubject, distinctUntilChanged, filter, map, of, Subject, switchMap, withLatestFrom } from 'rxjs';
import { JwtRefreshService } from '../../global-services/jwt-refresh.service';
import { JwtService } from '../../global-services/jwt.service';
import { IColumn } from '../../shared-components/data-grid/data-grid.component';
import { UpdateDataFn } from '../../shared-components/data-grid/data-grid.service';
import { statusDisplays } from '../../utils/status-display';




@Injectable()
export class ProcessOverviewService {

  readonly #destroy = new Subject<void>()

  readonly iconByStatus: Record<EProcessStatus, string> = {
    [EProcessStatus.Expired]: 'warning',
    [EProcessStatus.Cancelled]: 'cancel',
    [EProcessStatus.Draft]: 'construction',
    [EProcessStatus.Finished]: 'done_all',
    [EProcessStatus.InProgress]: 'pending',
    [EProcessStatus.Rejected]: 'block',
    [EProcessStatus.WaitingForMe]: 'schedule',
    [EProcessStatus.WaitingForOthers]: 'groups',
    [EProcessStatus.Inactive]: null,
    [EProcessStatus.None]: null
  }

  readonly colorByStatus: Record<EProcessStatus, ThemePalette> = {
    [EProcessStatus.Expired]: 'warn',
    [EProcessStatus.Cancelled]: 'warn',
    [EProcessStatus.Draft]: null,
    [EProcessStatus.Finished]: 'accent',
    [EProcessStatus.InProgress]: 'primary',
    [EProcessStatus.Rejected]: 'warn',
    [EProcessStatus.WaitingForMe]: 'primary',
    [EProcessStatus.WaitingForOthers]: 'primary',
    [EProcessStatus.Inactive]: null,
    [EProcessStatus.None]: null
  }

  readonly columns: IColumn<IProcessDto>[] = [
    {
      caption: 'ss-entities.process.title',
      dataField: 'description',
      type: 'string',
      title: true,
    },
    {
      caption: 'ss-entities.process.created',
      dataField: 'created',
      icon: 'edit_calendar',
      type: 'date',
      showInCollapsed: true
    },

    {
      caption: 'ss-entities.process.status-changed',
      dataField: 'statusChanged',
      type: 'date'
    },
    {
      caption: 'ss-entities.process.creator',
      dataField: 'creator',
      icon: 'person_add',
      showInCollapsed: true,
      type: 'string',
      calculateDisplay: (data) => data.creator ? data.creator.firstName + ' ' + data.creator.lastName : ''
    },
    {
      caption: 'ss-entities.process.valid-until',
      dataField: 'validTill',
      type: 'date',
      showInCollapsed: true,
      collapsedColor: (rowData) => {
        //TODO add c
        // to bi mi bila narančasta => Warning. A crvena =  isteklo.
        // Mislim da zelena ne treba.

        // dakle:
        // crvena = isteklo
        // narančasta => pred istek (stavio bih 3 dana)
        // plava => 3 dana do isteka

        const dayDiff = dateUtils.diffDays(new Date(rowData.validTill), new Date());
        if (dayDiff > 7) {
          return 'primary';
        }

        if (dayDiff > 2) {
          return 'accent'
        }

        return 'warn';
      },
      icon: 'pending_actions'
    },
    {
      caption: 'ss-entities.process.status',
      dataField: 'status',
      type: 'number',
      calculateDisplay: (data) => statusDisplays[data.status],
      icon: (data: IProcessDto) => this.iconByStatus[data.status],
      collapsedColor: (data: IProcessDto) => this.colorByStatus[data.status]
    },
    {
      caption: 'ss-entities.process.note',
      dataField: 'note',
      type: 'string'
    }
  ]


  readonly processActionKeys = [
    'process.cancel',
    'activity.reject',
    'process.delete',
    'process.download-audit-trail',
    'process.download-documents',
    'process.restart'
  ]

  readonly getRowLoaderKeys = (rowData: IProcessDto) => this.processActionKeys.map((actionKey) => [actionKey, rowData.id].join('.'))

  readonly statusSubject = new BehaviorSubject<EProcessStatus>(undefined)
  get status() {
    return this.statusSubject.value;
  }

  readonly loadObs = this.statusSubject.pipe(
    map((status) => (skip: number, take: number, search: string) => {

      search = search ? '%' + search + '%' : search;
      return this._processRemoteControllerService.getMany({
        page: skip / take,
        size: take,
        description: search,
        creator: search,
        note: search,
        status: status as any
      })
    }

    )
  )

  readonly columnsSubject = subjectFromObs({
    observable: this.statusSubject.pipe(
      map(status => status === EProcessStatus.InProgress || !status),
      distinctUntilChanged(),
      map((showStatus) => showStatus ?
        [...this.columns]
          .map(col => {
            if (col.dataField === 'status') {
              return { ...col, showInCollapsed: true }
            }
            return col
          })
        :
        this.columns
      )
    ),
    initialValue: this.columns,
    until: this.#destroy
  })



  get #jwt() {
    return this._jwtService.jwtSubject.value
  }


  readonly broadcastChannel = this._broadcastChannelService.createBroadcastChannel$(
    'processUpdateChannel',
    {
      processUpdated: () => createPersistantObservable(
        'process-update',
        () => this._jwtRefreshService.loggedInObs.pipe(
          map((loggedIn) => loggedIn ? this.#jwt : null),
          distinctUntilChanged(),
          switchMap((jwt) => {

            if (!jwt) {
              return of(null);
            }

            return this._serverSideEventRemoteControllerService.subscribeToProcessUpdates({ authorization: jwt })
          }),
          filter(Boolean)
        )
      )
    }
  )


  readonly processUpdateObs = this.broadcastChannel.processUpdated();

  readonly updateDataFn: UpdateDataFn = (getPrevItem) => this.processUpdateObs.pipe(
    withLatestFrom(this.statusSubject),
    map(([{ payload }, status]) => {

      const prevItem = getPrevItem(payload.id);
      if ((payload.status === status || !status) && !prevItem) {

        return { type: 'add', payload } as const;
      }

      if (prevItem && (payload.status !== status && status)) {
        return { type: 'remove', payload } as const;
      }

      return { type: 'update', payload } as const
    })
  )

  constructor(
    private readonly _processRemoteControllerService: ProcessRemoteControllerService,
    private readonly _serverSideEventRemoteControllerService: ServerSideEventRemoteControllerService,
    private readonly _jwtService: JwtService,
    private readonly _jwtRefreshService: JwtRefreshService,
    private readonly _broadcastChannelService: BroadcastChannelService
  ) { }




  ngOnDestroy() {
    this.#destroy.next()
    this.#destroy.complete()
  }

}
